import React from "react";
import { Link } from "react-router-dom";
import { Col, Container, Row } from "react-bootstrap";
import { Helmet } from "react-helmet";
import {
  gospelSteps,
  homeContent,
  identityCards,
  lifeTogetherCards,
  sermons,
  siteMeta,
  solas,
  visitDetails,
  visitHighlights,
} from "../data/siteContent";

function HomePage() {
  const [featuredSermon, ...recentSermons] = sermons;

  return (
    <>
      <Helmet>
        <title>{siteMeta.churchName}</title>
        <meta name="description" content={siteMeta.mission} />
      </Helmet>

      <section className="home-hero">
        <div className="home-hero__backdrop"></div>
        <Container>
          <Row className="align-items-center g-5">
            <Col lg={6}>
              <p className="section-kicker">Reformed Missional Church</p>
              <h1>{homeContent.hero.title}</h1>
              <p className="home-hero__intro">{homeContent.hero.text}</p>
              <div className="hero-actions">
                <Link to="/visit" className="site-button site-button--primary">
                  Plan Your Visit
                </Link>
                <Link to="/gospel" className="site-button site-button--secondary">
                  What Is the Gospel?
                </Link>
              </div>
            </Col>
            <Col lg={6}>
              <div className="hero-media-card">
                <img src="/landscape-1.jpg" alt="Church gathering" />
                <div className="hero-media-card__note">
                  <p>Christ-centered worship, faithful preaching, prayer, and fellowship.</p>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="site-section section-cream">
        <Container>
          <div className="section-heading">
            <p className="section-kicker">{homeContent.welcome.label}</p>
            <h2>{homeContent.welcome.title}</h2>
          </div>
          <Row className="g-4 align-items-start">
            <Col lg={8}>
              {homeContent.welcome.text.map((paragraph) => (
                <p className="section-copy" key={paragraph}>
                  {paragraph}
                </p>
              ))}
              <Link to="/about" className="site-button site-button--primary">
                Learn More About Us
              </Link>
            </Col>
            <Col lg={4}>
              <div className="editorial-aside">
                <span>Our central truth</span>
                <p>{siteMeta.mission}</p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="site-section">
        <Container>
          <div className="section-heading">
            <p className="section-kicker">Our Identity</p>
            <h2>Who We Are</h2>
          </div>
          <div className="identity-grid">
            {identityCards.map((item) => (
              <article key={item.heading} className="identity-card">
                <p className="identity-card__label">{item.label}</p>
                <h3>{item.heading}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </Container>
      </section>

      <section className="site-section section-dark">
        <Container>
          <div className="section-heading">
            <p className="section-kicker section-kicker--light">The Good News</p>
            <h2>What Is the Gospel?</h2>
            <p className="section-copy section-copy--light">
              The gospel is not advice about what we must do for God. It is the good
              news of what God has done for sinners through Jesus Christ.
            </p>
          </div>
          <div className="gospel-grid">
            {gospelSteps.map((step) => (
              <article key={step.number} className="gospel-card">
                <span>{step.number}</span>
                <small>{step.label}</small>
                <h3>{step.title}</h3>
                <p>{step.text}</p>
              </article>
            ))}
          </div>
          <div className="section-actions">
            <Link to="/gospel" className="site-button site-button--light">
              Read the Full Gospel Message
            </Link>
          </div>
        </Container>
      </section>

      <section className="site-section">
        <Container>
          <div className="section-heading">
            <p className="section-kicker">Listen to God's Word</p>
            <h2>Latest Sermon</h2>
          </div>
          <div className="featured-sermon-card">
            <div className="featured-sermon-card__media">
              <img src={featuredSermon.thumbnail} alt={featuredSermon.title} />
            </div>
            <div className="featured-sermon-card__content">
              <p className="meta-line">
                {featuredSermon.date} / {featuredSermon.preacher}
              </p>
              <h3>{featuredSermon.title}</h3>
              <p className="scripture-pill">{featuredSermon.passage}</p>
              <p>{featuredSermon.description}</p>
              <div className="button-row">
                <a
                  href={featuredSermon.audioUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="site-button site-button--primary"
                >
                  Play Audio
                </a>
                <a
                  href={featuredSermon.videoUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="site-button site-button--secondary"
                >
                  Watch Video
                </a>
              </div>
            </div>
          </div>
          <Row className="g-4 mt-2">
            {recentSermons.slice(0, 3).map((sermon) => (
              <Col md={4} key={sermon.id}>
                <article className="mini-sermon-card">
                  <img src={sermon.thumbnail} alt={sermon.title} loading="lazy" />
                  <div className="mini-sermon-card__body">
                    <p className="meta-line">
                      {sermon.date} / {sermon.preacher}
                    </p>
                    <h4>{sermon.title}</h4>
                    <p>{sermon.description}</p>
                  </div>
                </article>
              </Col>
            ))}
          </Row>
          <div className="section-actions">
            <Link to="/sermons" className="site-button site-button--secondary">
              Browse All Sermons
            </Link>
          </div>
        </Container>
      </section>

      <section className="site-section section-cream">
        <Container>
          <div className="section-heading">
            <p className="section-kicker">The Life of the Church</p>
            <h2>Life Together in Christ</h2>
          </div>
          <Row className="g-4">
            {lifeTogetherCards.map((item) => (
              <Col md={6} xl={3} key={item.title}>
                <article className="simple-card h-100">
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </article>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      <section className="site-section mission-statement">
        <Container>
          <p className="section-kicker">Our Mission</p>
          <h2>{siteMeta.mission}</h2>
          <blockquote>
            “Him we proclaim, warning everyone and teaching everyone with all wisdom,
            that we may present everyone mature in Christ.”
          </blockquote>
          <cite>Colossians 1:28</cite>
        </Container>
      </section>

      <section className="site-section">
        <Container>
          <div className="section-heading">
            <p className="section-kicker">Our Reformed Heritage</p>
            <h2>Five Truths That Shape Our Faith</h2>
          </div>
          <Row className="g-4">
            {solas.map((item) => (
              <Col md={6} xl={2} key={item.latin}>
                <article className="sola-card h-100">
                  <span>{item.latin}</span>
                  <h3>{item.english}</h3>
                </article>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      <section className="site-section section-cream">
        <Container>
          <Row className="g-5 align-items-start">
            <Col lg={6}>
              <div className="section-heading">
                <p className="section-kicker">You Are Welcome</p>
                <h2>Come and Worship With Us</h2>
              </div>
              <p className="section-copy">
                You do not need to have everything figured out before coming to church.
                Whether you have followed Christ for many years or are simply exploring
                Christianity, you are welcome to visit us.
              </p>
              <div className="visit-summary">
                {visitDetails.map((item) => (
                  <div key={item.label} className="visit-summary__item">
                    <strong>{item.label}</strong>
                    <span>{item.value}</span>
                  </div>
                ))}
              </div>
            </Col>
            <Col lg={6}>
              <div className="visit-card">
                <h3>What to Expect</h3>
                <ul>
                  {visitHighlights.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                <div className="button-row">
                  <Link to="/visit" className="site-button site-button--primary">
                    Plan Your Visit
                  </Link>
                  <a
                    href={siteMeta.directionsUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="site-button site-button--secondary"
                  >
                    Get Directions
                  </a>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="site-section final-cta">
        <Container>
          <div className="final-cta__content">
            <h2>Come and See What God Has Done in Jesus Christ</h2>
            <p>
              Join us as we gather around God's Word, grow together in Christ, and
              live on mission for His glory.
            </p>
            <div className="button-row">
              <Link to="/visit" className="site-button site-button--primary">
                Plan Your Visit
              </Link>
              <Link to="/gospel" className="site-button site-button--secondary">
                Explore the Gospel
              </Link>
            </div>
          </div>
        </Container>
      </section>
    </>
  );
}

export default HomePage;
